/**
 * visibility.ts — v0M5.1 (2026-07-25) — Per-faction fog-of-war + BFS LoS.
 *
 * Implements the four-state visibility model per `MAP.md` §7:
 *
 *   1 — Unexplored          (bit 0 = 0)
 *   2 — Explored, no vision (bit 0 = 1, bits 1+2 = 0)
 *   3 — Within vision range (bit 0 = 1, bit 1 = 1, bit 2 = 0)
 *   4 — Vision + Detection  (bit 0 = 1, bit 1 = 1, bit 2 = 1)
 *
 * Internal storage: one byte per cell per faction, with three flag bits.
 * The `explored` bit is sticky (once set, stays); `visible` and
 * `detected` are transient and cleared before every recompute.
 *
 * Recompute cadence: once per world tick, ordered after `ai` and before
 * `victory` (see `systems.ts`). BFS from every viewer per faction:
 *
 *   - Armies contribute `army.visionRange` (or `army_vision_range` dial
 *     when the field is unset) as the BFS budget.
 *   - Settlements contribute `settlement.visionRange` (or
 *     `settlement_vision_range` dial) — owner-only; neutral settlements
 *     contribute nothing.
 *   - Spies + future viewers layer on when their entities land.
 *
 * Terrain cost per cell comes from `vision_cost_default` +
 * per-terrain override dials (`vision_cost_forest`,
 * `vision_cost_mountain`, ...). Costs ≥ `HARD_BLOCKER_THRESHOLD` (100)
 * treat the cell as an opaque wall — BFS never crosses it, and the
 * cell itself only becomes visible if the viewer stands on it (or
 * inside Forest at `forest_interior_sight_range`).
 *
 * Bonuses:
 *   - Viewer standing on Hills: budget += `hills_viewer_bonus_radius`.
 *   - Viewer inside Forest: BFS budget is CAPPED to
 *     `forest_interior_sight_range` (short interior sight per MAP.md §1.1).
 *
 * Team pooling (`MAP.md` §7.4): after per-faction recompute, teammate
 * maps are OR-ed together — every teammate sees the union of their
 * team's visible + explored + detection state.
 *
 * Determinism: iteration via `sortedIds()` for both viewers and
 * factions. RNG unused (no rolls in visibility).
 */

import type { Command } from './commands';
import type { GameState, FactionId, Settlement, Faction } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import { sortedIds } from './state';
import { getDialNumber, loadDials, type Dials } from '../data/dials';
// Note: `getDialNumber` retained for the null-returning helper below —
// see getDialNumberOrNull.
import { loadSpecialUnits, type SpecialUnitSpec } from '../data/special-units';
import { loadMapStructures } from '../data/map-structures';

// v0M5.3 (2026-07-25) — SpecialUnits cache for the spy vision loop.
// Loaded once at module init; consulted per spy per tick.
const VIS_SPECIAL_UNITS = loadSpecialUnits();
function getSpySpec(name: string): SpecialUnitSpec | undefined {
  return VIS_SPECIAL_UNITS[name];
}

// v0M7.4a (2026-07-25) — MapStructures cache for the Watch Tower
// vision loop. Loaded once at module init; consulted per tower per
// tick to resolve `visionRange`. Falls back to `watchtowerDefault`
// (dial `world_watchtower_vision_range`, default 3) when the xlsx
// spec is absent.
const VIS_MAP_STRUCTURES = loadMapStructures();

// ---------------------------------------------------------------------------
// Bit-flag storage + 4-state accessors
// ---------------------------------------------------------------------------

/** Sticky "have we ever seen this cell" bit. */
const BIT_EXPLORED = 0b0001;
/** Transient "in vision this tick" bit. */
const BIT_VISIBLE = 0b0010;
/** Transient "revealed by a Detection-tagged viewer this tick" bit. */
const BIT_DETECTED = 0b0100;

/** Named 4-state values returned by `getCellState()`. Kept as literals
 *  so renderer switch statements are exhaustive. Values match the state
 *  numbering in `MAP.md` §7.1 for legibility. */
export const CELL_STATE_UNEXPLORED = 1 as const;
export const CELL_STATE_EXPLORED = 2 as const;
export const CELL_STATE_VISIBLE = 3 as const;
export const CELL_STATE_DETECTED = 4 as const;
export type CellState =
  | typeof CELL_STATE_UNEXPLORED
  | typeof CELL_STATE_EXPLORED
  | typeof CELL_STATE_VISIBLE
  | typeof CELL_STATE_DETECTED;

/** Cost value at or above which BFS treats a cell as an opaque wall —
 *  vision doesn't enter, doesn't cross. Sentinel-style; dial authors
 *  encode blockers with `vision_cost_<terrain> = 999`. */
export const HARD_BLOCKER_THRESHOLD = 100;

/** MFix8c (2026-07-25) — √2 distance multiplier for diagonal BFS steps.
 *  Makes the vision shape octagonal instead of square (Option 1 of the
 *  fog-shape discussion). Reads as circular at typical zoom + fog
 *  smoothing. Cached module-level to skip Math.sqrt calls in the inner
 *  BFS loop. */
const SQRT2 = Math.SQRT2;

// ---------------------------------------------------------------------------
// Terrain id → dial-name mapping (sim-side; mirrors the client-side enum)
// ---------------------------------------------------------------------------

/** Terrain ids match the client `Terrain` enum in
 *  `src/client/map/terrain-palette.ts`. Kept sim-side (as a const array
 *  keyed by id) so `sim/` doesn't import from `client/`. Extend when
 *  Michael adds new terrain types. */
const TERRAIN_ID_TO_DIAL_NAME: readonly string[] = [
  'plains',    // 0
  'farmland',  // 1
  'forest',    // 2
  'hills',     // 3
  'mountain',  // 4  (dial name uses singular — matches Michael's authoring)
  'desert',    // 5
  'swamp',     // 6
  'lake',      // 7
  'sea',       // 8
];

/** Forest id — needed for the interior-sight rule. */
const TERRAIN_ID_FOREST = 2;
/** Hills id — needed for the viewer-elevation bonus. */
const TERRAIN_ID_HILLS = 3;

// ---------------------------------------------------------------------------
// VisibilityMap — per-faction cell-state storage
// ---------------------------------------------------------------------------

export interface VisibilityMap {
  readonly width: number;
  readonly height: number;
  /** Row-major byte array; each byte packs the three flag bits above. */
  readonly cells: Uint8Array;
}

/** Fresh map with every cell unexplored (state 1). Match sim-context
 *  terrain dimensions. */
export function createUnexploredVisibilityMap(width: number, height: number): VisibilityMap {
  return { width, height, cells: new Uint8Array(width * height) };
}

/** Fresh map with every cell explored (state 2) — used when the New
 *  Game "Map Explored" option is set. */
export function createExploredVisibilityMap(width: number, height: number): VisibilityMap {
  const cells = new Uint8Array(width * height);
  cells.fill(BIT_EXPLORED);
  return { width, height, cells };
}

/** Read the current 4-state value for one cell. Returns UNEXPLORED for
 *  out-of-bounds coordinates. */
export function getCellState(map: VisibilityMap, x: number, y: number): CellState {
  if (x < 0 || y < 0 || x >= map.width || y >= map.height) return CELL_STATE_UNEXPLORED;
  const b = map.cells[y * map.width + x]!;
  if ((b & BIT_EXPLORED) === 0) return CELL_STATE_UNEXPLORED;
  if ((b & BIT_VISIBLE) === 0) return CELL_STATE_EXPLORED;
  if ((b & BIT_DETECTED) === 0) return CELL_STATE_VISIBLE;
  return CELL_STATE_DETECTED;
}

/** Count cells at each state — used by dev-tools "Print visibility stats" +
 *  the visibility test suite. Returns a fixed-order tuple. */
export function countCellStates(map: VisibilityMap): {
  readonly unexplored: number;
  readonly explored: number;
  readonly visible: number;
  readonly detected: number;
} {
  let unexplored = 0, explored = 0, visible = 0, detected = 0;
  for (let i = 0; i < map.cells.length; i++) {
    const b = map.cells[i]!;
    if ((b & BIT_EXPLORED) === 0) { unexplored++; continue; }
    if ((b & BIT_VISIBLE) === 0) { explored++; continue; }
    if ((b & BIT_DETECTED) === 0) { visible++; continue; }
    detected++;
  }
  return { unexplored, explored, visible, detected };
}

// ---------------------------------------------------------------------------
// Vision cost table — built once from the dials snapshot
// ---------------------------------------------------------------------------

interface VisionConfig {
  readonly costByTerrain: readonly number[];
  readonly defaultCost: number;
  readonly forestInteriorSight: number;
  readonly hillsBonusRadius: number;
  readonly armyDefault: number;
  readonly settlementDefault: number;
  /** v0M7.4a (2026-07-25) — Watch Tower default vision range (cells)
   *  when the MapStructures spec is absent. Corresponds to the xlsx
   *  `visionRange` column on the Watch Tower row. */
  readonly watchtowerDefault: number;
}

let cachedConfig: VisionConfig | null = null;
let cachedConfigDials: Dials | null = null;

/**
 * Build (once, memoised) the vision-cost lookup table from the current
 * dials snapshot. If `dials` changes identity between calls, the cache
 * is rebuilt — supports the dev-tools hot-swap case + test isolation.
 */
function loadVisionConfig(): VisionConfig {
  const dials = loadDials();
  if (cachedConfig !== null && cachedConfigDials === dials) return cachedConfig;
  // All vision dials use the null-returning accessor so a stale dials.json
  // (from before `npm run data:build` regenerates it against Michael's
  // new xlsx rows) doesn't throw — the sim falls back to sane defaults
  // and the system keeps running for tests + dev. Tests that pre-date
  // v0M5.1 don't set up the vision dials and shouldn't be forced to.
  const defaultCost = readDialOrFallback(dials, 'vision_cost_default', 1);
  const costByTerrain: number[] = TERRAIN_ID_TO_DIAL_NAME.map((terrainName) => {
    const dialName = `vision_cost_${terrainName}`;
    const raw = getDialNumberOrNull(dials, dialName);
    return raw === null ? defaultCost : raw;
  });
  const config: VisionConfig = {
    costByTerrain,
    defaultCost,
    forestInteriorSight: readDialOrFallback(dials, 'forest_interior_sight_range', 2),
    hillsBonusRadius: readDialOrFallback(dials, 'hills_viewer_bonus_radius', 2),
    armyDefault: readDialOrFallback(dials, 'army_vision_range', 6),
    settlementDefault: readDialOrFallback(dials, 'settlement_vision_range', 5),
    watchtowerDefault: readDialOrFallback(dials, 'world_watchtower_vision_range', 3),
  };
  cachedConfig = config;
  cachedConfigDials = dials;
  return config;
}

/**
 * v0M5.1 defensive helper — read a dial by name, or fall back to a
 * sane default if the dial is missing OR its value is non-positive /
 * non-finite. Consolidates the "getDialNumber-throws" mitigation.
 */
function readDialOrFallback(dials: Dials, name: string, fallback: number): number {
  const raw = getDialNumberOrNull(dials, name);
  return raw === null ? fallback : numberOrFallback(raw, fallback);
}

function numberOrFallback(n: number, fallback: number): number {
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** getDialNumber throws for unknown dials; test with a null-returning
 *  variant so per-terrain overrides can be optional. */
function getDialNumberOrNull(dials: Dials, name: string): number | null {
  if (!(name in dials)) return null;
  try {
    return getDialNumber(dials, name);
  } catch {
    return null;
  }
}

/** Cost to enter one cell for BFS purposes, given the cell's terrain id. */
function costForTerrain(config: VisionConfig, terrainId: number): number {
  return config.costByTerrain[terrainId] ?? config.defaultCost;
}

// ---------------------------------------------------------------------------
// Per-viewer BFS
// ---------------------------------------------------------------------------

/** Mutable BFS scratch. Allocated once per system call and reused across
 *  viewers to avoid GC pressure. Sized to the map dimensions.  */
interface BfsScratch {
  readonly bestCost: Float32Array;
  readonly visited: Uint8Array;
  frontier: number[];
  nextFrontier: number[];
}

function createScratch(width: number, height: number): BfsScratch {
  return {
    bestCost: new Float32Array(width * height),
    visited: new Uint8Array(width * height),
    frontier: [],
    nextFrontier: [],
  };
}

function resetScratch(s: BfsScratch): void {
  s.bestCost.fill(Number.POSITIVE_INFINITY);
  s.visited.fill(0);
  s.frontier.length = 0;
  s.nextFrontier.length = 0;
}

/**
 * BFS from `(originX, originY)` outward. For every cell reached within
 * `budget` cumulative terrain cost, set BIT_VISIBLE + BIT_EXPLORED on
 * the passed VisibilityMap. If `withDetection`, also set BIT_DETECTED.
 *
 * Origin cell is always visible regardless of terrain (a viewer on
 * Mountains sees its own cell). This is the sole exception to the
 * hard-blocker rule.
 */
function bfsFromViewer(
  vis: VisibilityMap,
  ctx: SimContext,
  config: VisionConfig,
  scratch: BfsScratch,
  originX: number,
  originY: number,
  budget: number,
  withDetection: boolean,
): void {
  const { width, height } = vis;
  if (originX < 0 || originY < 0 || originX >= width || originY >= height) return;

  resetScratch(scratch);

  const originIdx = originY * width + originX;
  const originTerrain = ctx.terrain.terrain[originIdx] ?? 0;

  // Forest-interior cap: viewer inside Forest sees only a short radius.
  let effectiveBudget = budget;
  if (originTerrain === TERRAIN_ID_FOREST) {
    effectiveBudget = Math.min(effectiveBudget, config.forestInteriorSight);
  }
  // Hills bonus: viewer on Hills sees further.
  if (originTerrain === TERRAIN_ID_HILLS) {
    effectiveBudget += config.hillsBonusRadius;
  }
  if (effectiveBudget <= 0) {
    // Still mark the origin cell.
    markCell(vis.cells, originIdx, withDetection);
    return;
  }

  scratch.bestCost[originIdx] = 0;
  scratch.frontier.push(originIdx);
  markCell(vis.cells, originIdx, withDetection);

  while (scratch.frontier.length > 0) {
    for (const idx of scratch.frontier) {
      const px = idx % width;
      const py = (idx - px) / width;
      const currentCost = scratch.bestCost[idx]!;

      // 8-neighbourhood. MFix8c (2026-07-25): diagonal steps cost √2×
      // the terrain-entry cost per Option 1 of the fog-shape discussion.
      // Effect: vision expands as an octagon rather than a square, which
      // combined with the bilinear-smoothed fog render (fog-render.ts)
      // reads as a soft circle. Hard-blocker check still uses the raw
      // terrain enterCost (not multiplied) — a blocker is a blocker
      // regardless of approach direction. If Option 2 (Euclidean gate)
      // is added later, layer it as a post-BFS filter here.
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = px + dx;
          const ny = py + dy;
          if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
          const nIdx = ny * width + nx;
          const nTerrain = ctx.terrain.terrain[nIdx] ?? 0;
          const enterCost = costForTerrain(config, nTerrain);
          const isDiagonal = dx !== 0 && dy !== 0;
          const stepCost = isDiagonal ? enterCost * SQRT2 : enterCost;
          const nCost = currentCost + stepCost;
          if (nCost > effectiveBudget) continue;
          if (nCost >= scratch.bestCost[nIdx]!) continue;
          scratch.bestCost[nIdx] = nCost;
          markCell(vis.cells, nIdx, withDetection);
          // Only re-expand if the cell isn't itself a hard blocker.
          // Blockers still become visible from the outside (we saw the
          // face of the mountain), but vision doesn't propagate through.
          if (enterCost >= HARD_BLOCKER_THRESHOLD) continue;
          scratch.nextFrontier.push(nIdx);
        }
      }
    }
    scratch.frontier = scratch.nextFrontier;
    scratch.nextFrontier = [];
  }
}

function markCell(cells: Uint8Array, idx: number, withDetection: boolean): void {
  cells[idx] = cells[idx]! | BIT_EXPLORED | BIT_VISIBLE | (withDetection ? BIT_DETECTED : 0);
}

// ---------------------------------------------------------------------------
// Team pooling
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// System entry — called each world tick from systems.ts
// ---------------------------------------------------------------------------

export function visibilitySystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  context: SimContext,
): GameState {
  if (!context.terrain || context.terrain.width <= 0 || context.terrain.height <= 0) {
    return state;
  }
  const { width, height } = context.terrain;
  const config = loadVisionConfig();

  // Ensure per-faction map exists — lazy-init preserves back-compat with
  // fixtures that skip scenario bootstrap.
  if (!state.visibility) state.visibility = new Map();
  for (const factionId of sortedIds(state.factions)) {
    const faction = state.factions.get(factionId as FactionId)!;
    if (isNeutralFaction(faction)) continue;
    if (!state.visibility.has(faction.id)) {
      state.visibility.set(faction.id, createUnexploredVisibilityMap(width, height));
    }
  }

  // Step 1 — clear transient bits (visible + detected). Explored bit stays.
  for (const vis of state.visibility.values()) {
    for (let i = 0; i < vis.cells.length; i++) {
      vis.cells[i] = vis.cells[i]! & BIT_EXPLORED;
    }
  }

  // Step 2 — per-faction BFS from every viewer.
  const scratch = createScratch(width, height);
  for (const factionId of sortedIds(state.factions)) {
    const faction = state.factions.get(factionId as FactionId);
    if (!faction || isNeutralFaction(faction)) continue;
    const vis = state.visibility.get(faction.id);
    if (!vis) continue;

    // Settlements — owner-only. Fall back to dial when visionRange is unset.
    for (const settlementId of sortedIds(state.settlements)) {
      const settlement = state.settlements.get(settlementId as ReturnType<typeof toSettlementIdShim>);
      if (!settlement) continue;
      if (!settlementBelongsToFaction(state, settlement, faction.id)) continue;
      const range = settlement.visionRange ?? config.settlementDefault;
      const withDetection = false; // No settlement carries Detection at v1.
      bfsFromViewer(vis, context, config, scratch, settlement.cell.x, settlement.cell.y, range, withDetection);
    }

    // Armies.
    for (const armyId of sortedIds(state.armies)) {
      const army = state.armies.get(armyId as ReturnType<typeof toArmyIdShim>);
      if (!army || army.ownerFactionId !== faction.id) continue;
      const range = army.visionRange ?? config.armyDefault;
      const withDetection = false; // No army carries Detection at v1 (Spies land at v0M5.3).
      bfsFromViewer(vis, context, config, scratch, Math.floor(army.position.x), Math.floor(army.position.y), range, withDetection);
    }

    // v0M5.3 — Spies. Each spy contributes vision from the SpecialUnits
    // xlsx row (Spy → visionRange 2). The Detection ability tag raises
    // affected cells from state 3 to state 4, revealing invisibles
    // (other spies). Own-spy propagation always fires; enemy spies are
    // filtered out of iteration because we're inside the per-faction
    // loop for `faction.id === ownerFactionId`.
    if (state.spies) {
      for (const spyId of sortedIds(state.spies)) {
        const spy = state.spies.get(spyId as ReturnType<typeof toSpyIdShim>);
        if (!spy || spy.ownerFactionId !== faction.id) continue;
        // v0M5.4 — spies inside settlements don't contribute vision to
        // their cell (they're not on the map). Intel from inside comes
        // from the read-only settlement panel, not the visibility map.
        if (spy.insideSettlementId) continue;
        const spec = getSpySpec(spy.specialUnitType);
        const range = spec?.visionRange ?? 2;
        // Detection is a Spy trait at v1 → withDetection=true whenever
        // the SpecialUnits row's abilities list contains 'Detection'.
        const hasDetection = spec?.abilities.includes('Detection') ?? false;
        bfsFromViewer(
          vis,
          context,
          config,
          scratch,
          Math.floor(spy.position.x),
          Math.floor(spy.position.y),
          range,
          hasDetection,
        );
      }
    }

    // v0M7.4a (2026-07-25) — Watch Tower vision. Each owned Watch
    // Tower contributes vision from the MapStructures xlsx row
    // (Watch Tower → visionRange 3 at v1). Owner-only iteration —
    // enemy towers propagate under their own owner's faction pass.
    // Fort + Road + Bridge do not contribute vision at v1 (Road /
    // Bridge are unowned; Fort's vision is a follow-up if playtest
    // asks for it).
    if (state.worldStructures) {
      for (const ws of state.worldStructures.values()) {
        if (ws.kind !== 'watchtower') continue;
        if (ws.ownerFactionId !== faction.id) continue;
        const spec = VIS_MAP_STRUCTURES['Watch Tower'];
        const range = spec?.visionRange ?? config.watchtowerDefault;
        if (range <= 0) continue;
        // No Detection at v1 — Watch Towers don't see through Invisibility.
        bfsFromViewer(vis, context, config, scratch, ws.cell.x, ws.cell.y, range, false);
      }
    }
  }

  // Step 3 — team pooling: teammates OR their maps together.
  poolTeamVisibility(state);

  return state;
}

/**
 * A faction counts as "neutral" (and gets no visibility map) if it has
 * no owned settlements AND no armies — the sentinel wilderness faction
 * from `scenario.ts` sits in this bucket. Cheap check; adjust if a
 * cleaner isNeutral flag lands on Faction.
 */
function isNeutralFaction(faction: Faction): boolean {
  // Session-28c uses a sentinel high faction id for wilderness. If the
  // faction has ownedSettlements or ownedArmies fields in future, key
  // off those; for now, the neutral sentinel is created without an
  // `isAI` differentiator — treat any faction without teamId as neutral.
  return faction.teamId < 0;
}

function settlementBelongsToFaction(state: GameState, settlement: Settlement, factionId: FactionId): boolean {
  const province = state.provinces.get(settlement.provinceId);
  return province?.ownerFactionId === factionId;
}

/** After per-faction BFS, OR together every teammate's visibility map. */
function poolTeamVisibility(state: GameState): void {
  if (!state.visibility) return;
  // Group faction ids by teamId. Skip teams with a single member (nothing
  // to pool).
  const teams = new Map<number, FactionId[]>();
  for (const factionId of sortedIds(state.factions)) {
    const faction = state.factions.get(factionId as FactionId);
    if (!faction || isNeutralFaction(faction)) continue;
    if (!state.visibility.has(faction.id)) continue;
    const list = teams.get(faction.teamId) ?? [];
    list.push(faction.id);
    teams.set(faction.teamId, list);
  }
  for (const list of teams.values()) {
    if (list.length < 2) continue;
    // Sum every member's bits into a scratch buffer, then write back to
    // each member so subsequent poolInto calls don't compound.
    const first = state.visibility.get(list[0]!)!;
    const scratch = new Uint8Array(first.cells.length);
    scratch.set(first.cells);
    for (let i = 1; i < list.length; i++) {
      const m = state.visibility.get(list[i]!)!;
      for (let j = 0; j < scratch.length; j++) {
        scratch[j] = scratch[j]! | m.cells[j]!;
      }
    }
    for (const fid of list) {
      const m = state.visibility.get(fid)!;
      m.cells.set(scratch);
    }
  }
}

// Local Type-only shims to bridge the toSettlementId / toArmyId branding
// used by `sortedIds`. They exist purely for TS satisfaction; runtime
// values are plain numbers.
type SettlementIdShim = { readonly __brand: 'SettlementId' } & number;
type ArmyIdShim = { readonly __brand: 'ArmyId' } & number;
type SpyIdShim = { readonly __brand: 'SpyId' } & number;
function toSettlementIdShim(x: number): SettlementIdShim { return x as SettlementIdShim; }
function toArmyIdShim(x: number): ArmyIdShim { return x as ArmyIdShim; }
function toSpyIdShim(x: number): SpyIdShim { return x as SpyIdShim; }
